import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { LogOut, Plus, Users, TrendingUp, AlertTriangle, XCircle, FileText, Trash2, Edit } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Sidebar from '../components/Sidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  'Normal': 'bg-green-100 text-green-800',
  'Alerta Amarelo': 'bg-yellow-100 text-yellow-800',
  'Alerta Vermelho': 'bg-red-100 text-red-800',
  'Descontinuado': 'bg-gray-100 text-gray-800'
};

export default function DashboardAdmin({ user, onLogout }) {
  const navigate = useNavigate();
  const [painelData, setPainelData] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [newCorretor, setNewCorretor] = useState({
    nome: '',
    email: '',
    data_inicio: new Date().toISOString().split('T')[0],
    mentor_id: '',
    mentor_nome: ''
  });
  const [newUser, setNewUser] = useState({
    nome: '',
    email: '',
    senha: '',
    role: 'corretor'
  });

  useEffect(() => {
    loadData();
  }, []);

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/corretores/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `corretores_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
      console.error(error);
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Apenas arquivos Excel (.xlsx, .xls) são permitidos');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/corretores/import/excel`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Importação concluída! ${response.data.added} adicionados, ${response.data.updated} atualizados`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
      console.error(error);
    }

    event.target.value = '';
  };

  const loadData = async () => {
    try {
      const [painelRes, statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/painel`),
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/users`)
      ]);
      setPainelData(painelRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCorretor = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/corretores`, newCorretor);
      toast.success('Corretor cadastrado com sucesso!');
      setIsDialogOpen(false);
      setNewCorretor({
        nome: '',
        email: '',
        data_inicio: new Date().toISOString().split('T')[0],
        mentor_id: '',
        mentor_nome: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar corretor');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, newUser);
      toast.success('Usuário cadastrado com sucesso!');
      setIsUserDialogOpen(false);
      setNewUser({
        nome: '',
        email: '',
        senha: '',
        role: 'corretor'
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar usuário');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) {
      return;
    }
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Usuário excluído com sucesso!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const handleDeleteCorretor = async (corretorId) => {
    if (!window.confirm('Tem certeza que deseja excluir este corretor?')) {
      return;
    }
    try {
      await axios.delete(`${API}/corretores/${corretorId}`);
      toast.success('Corretor excluído com sucesso!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir corretor');
    }
  };

  const filteredData = painelData.filter(item =>
    item.corretor.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.corretor.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    u.nome.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.email.toLowerCase().includes(searchUser.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-remax-blue"></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F3F4F6' }}>
      <Sidebar currentPage="dashboard" user={user} onLogout={onLogout} />
      
      <div style={{ flex: 1, overflow: 'auto' }}>
        {/* Header */}
        <header className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Dashboard Administrativo</h1>
                  <p className="text-sm text-gray-500">Gestão completa do Silver Start</p>
                </div>
              </div>
            </div>
          </div>
        </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card data-testid="stat-card-total">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Corretores</CardTitle>
                <Users className="h-4 w-4 text-remax-blue" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" style={{ color: '#003DA5' }}>{stats.total_corretores}</div>
                <p className="text-xs text-gray-500 mt-1">No programa</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-card-captacoes">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Captações Total</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.captacoes_total}</div>
                <p className="text-xs text-gray-500 mt-1">Média: {stats.media_captacoes_por_corretor}</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-card-alertas">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Alertas</CardTitle>
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">
                  {stats.corretores_alerta_amarelo + stats.corretores_alerta_vermelho}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Amarelo: {stats.corretores_alerta_amarelo} | Vermelho: {stats.corretores_alerta_vermelho}
                </p>
              </CardContent>
            </Card>

            <Card data-testid="stat-card-users">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Usuários</CardTitle>
                <Users className="h-4 w-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">{users.length}</div>
                <p className="text-xs text-gray-500 mt-1">Todos os usuários</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="corretores" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="corretores">Corretores</TabsTrigger>
            <TabsTrigger value="usuarios">Usuários</TabsTrigger>
          </TabsList>

          {/* Corretores Tab */}
          <TabsContent value="corretores">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                  <CardTitle style={{ fontFamily: 'Work Sans', color: '#003DA5' }}>
                    PAINEL GERAL - ONBOARDING 90 DIAS
                  </CardTitle>
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Buscar corretor..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-64"
                      data-testid="search-input"
                    />
                    <Button 
                      onClick={handleExportExcel}
                      variant="outline"
                      className="bg-green-50 hover:bg-green-100 border-green-300"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Exportar Relatório
                    </Button>
                    <label>
                      <Button 
                        variant="outline"
                        className="bg-blue-50 hover:bg-blue-100 border-blue-300"
                        onClick={() => document.getElementById('import-excel-input').click()}
                        type="button"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Importar Relatório
                      </Button>
                      <input
                        id="import-excel-input"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleImportExcel}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-remax-blue hover:bg-[#002C7A]" data-testid="add-corretor-button">
                          <Plus className="w-4 h-4 mr-2" />
                          Novo Corretor
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Cadastrar Novo Corretor</DialogTitle>
                          <DialogDescription>Preencha os dados do corretor para iniciar o onboarding</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreateCorretor} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="nome">Nome Completo</Label>
                            <Input
                              id="nome"
                              value={newCorretor.nome}
                              onChange={(e) => setNewCorretor({ ...newCorretor, nome: e.target.value })}
                              required
                              data-testid="corretor-nome-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              type="email"
                              value={newCorretor.email}
                              onChange={(e) => setNewCorretor({ ...newCorretor, email: e.target.value })}
                              required
                              data-testid="corretor-email-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="data_inicio">Data de Início</Label>
                            <Input
                              id="data_inicio"
                              type="date"
                              value={newCorretor.data_inicio}
                              onChange={(e) => setNewCorretor({ ...newCorretor, data_inicio: e.target.value })}
                              required
                              data-testid="corretor-data-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="mentor_nome">Nome do Mentor/Broker</Label>
                            <Input
                              id="mentor_nome"
                              value={newCorretor.mentor_nome}
                              onChange={(e) => setNewCorretor({ ...newCorretor, mentor_nome: e.target.value })}
                              placeholder="Opcional"
                              data-testid="corretor-mentor-input"
                            />
                          </div>
                          <Button type="submit" className="w-full bg-remax-blue hover:bg-[#002C7A]" data-testid="submit-corretor-button">
                            Cadastrar
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="painel-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Corretor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Início</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Semana</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Módulo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mentor</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">CRE</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredData.map((item, index) => (
                        <tr key={item.corretor.id} className="hover:bg-gray-50" data-testid={`corretor-row-${index}`}>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{item.corretor.nome}</div>
                              <div className="text-sm text-gray-500">{item.corretor.email}</div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(item.corretor.data_inicio).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                            {item.corretor.semana_atual}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium">
                            {item.corretor.modulo_atual}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.corretor.mentor_nome || '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-semibold text-green-600">
                            {item.corretor.captacoes_acumuladas}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[item.corretor.status]}`}>
                              {item.corretor.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                            <div className="flex items-center justify-center space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/ficha-semanal/${item.corretor.id}`)}
                                data-testid={`view-scorecard-${index}`}
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCorretor(item.corretor.id)}
                                className="text-red-600 hover:text-red-800"
                                data-testid={`delete-corretor-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredData.length === 0 && (
                    <div className="text-center py-12 text-gray-500" data-testid="no-data-message">
                      Nenhum corretor encontrado
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Usuarios Tab */}
          <TabsContent value="usuarios">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                  <CardTitle style={{ fontFamily: 'Work Sans', color: '#003DA5' }}>
                    GERENCIAR USUÁRIOS
                  </CardTitle>
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Buscar usuário..."
                      value={searchUser}
                      onChange={(e) => setSearchUser(e.target.value)}
                      className="w-64"
                      data-testid="search-user-input"
                    />
                    <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-remax-blue hover:bg-[#002C7A]" data-testid="add-user-button">
                          <Plus className="w-4 h-4 mr-2" />
                          Novo Usuário
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Cadastrar Novo Usuário</DialogTitle>
                          <DialogDescription>Preencha os dados do usuário</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreateUser} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="user-nome">Nome Completo</Label>
                            <Input
                              id="user-nome"
                              value={newUser.nome}
                              onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })}
                              required
                              data-testid="user-nome-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="user-email">Email</Label>
                            <Input
                              id="user-email"
                              type="email"
                              value={newUser.email}
                              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                              required
                              data-testid="user-email-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="user-senha">Senha</Label>
                            <Input
                              id="user-senha"
                              type="password"
                              value={newUser.senha}
                              onChange={(e) => setNewUser({ ...newUser, senha: e.target.value })}
                              required
                              data-testid="user-senha-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="user-role">Tipo de Conta</Label>
                            <select
                              id="user-role"
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                              value={newUser.role}
                              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                              data-testid="user-role-select"
                            >
                              <option value="corretor">Corretor</option>
                              <option value="broker">Broker/Mentor</option>
                              <option value="gestor">Gestor</option>
                              <option value="administrador">Administrador</option>
                            </select>
                          </div>
                          <Button type="submit" className="w-full bg-remax-blue hover:bg-[#002C7A]" data-testid="submit-user-button">
                            Cadastrar
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="users-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data de Criação</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUsers.map((u, index) => (
                        <tr key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${index}`}>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {u.nome}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {u.email}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              u.role === 'administrador' ? 'bg-red-100 text-red-800' :
                              u.role === 'gestor' ? 'bg-blue-100 text-blue-800' :
                              u.role === 'broker' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(u.created_at).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(u.id)}
                              className="text-red-600 hover:text-red-800"
                              disabled={u.id === user.id}
                              data-testid={`delete-user-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && (
                    <div className="text-center py-12 text-gray-500" data-testid="no-users-message">
                      Nenhum usuário encontrado
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      </div>
    </div>
  );
}
